import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'

import { cueFor, playCue, preloadCues } from '../lib/cues'
import { outbox as defaultOutbox, newTakeId } from '../lib/outbox'
import { useAuthStore } from '../store/authStore'
import {
  AUTO_STOP_TAIL_MS,
  PLAYBACK_SPEEDS,
  autoStopReady,
  classifyFailure,
  hasTake,
  initialState,
  isLastRep,
  isQueuedAnswer,
  isRepeating,
  localPractice,
  practiceSentence,
  reduce,
  repNumber,
  restorable,
} from '../lib/practiceSession'
import { MIN_TAKE_SECONDS } from '../lib/practiceSession'
import { allWordsCheck, speechHeard } from '../lib/repeatMatch'
import { createReferenceGuard } from '../lib/referenceGuard'
import { mediaSourceFor } from '../lib/speedVariants'
import { useLearningStore } from '../store/learningStore'
import { useAudio } from './useAudio'
import { useLiveText } from './useLiveText'

/** After a checked repetition's cue has played, the breath before the reference plays again (ms). */
const REPLAY_AFTER_CUE_MS = 250

export { PLAYBACK_SPEEDS }

/** A practice of one take — what every caller gets until the server says otherwise. */
const NO_REPEAT = { count: 1, threshold: 0 }

/**
 * Drives one shadowing exercise: play the reference, record, review, submit.
 *
 * The session itself — which phase the take is in, the reference player,
 * the sending panel's step — is the pure reducer in `lib/practiceSession`.
 * This hook is the adapter around it: it owns the side effects (the
 * `<audio>`/`<video>` element, the microphone via `useAudio`, the submit
 * request via the learning store and its abort controller, and the outbox
 * in IndexedDB) and turns what they do into the reducer's plain actions.
 *
 * Reference playback and recording are kept separate on purpose. Recording
 * over the loudspeaker would put the native speaker's voice into the
 * learner's own track, and the assessment would then be scoring the
 * reference audio back to itself.
 *
 * The outbox (V9-w, `lib/outbox.js`) is the third side effect. A recording
 * is written to it the moment it exists, under the `clientAttemptId` every
 * upload of it carries, and removed once the server has it — scored (200)
 * or queued (202) — or once the learner discards it. Nothing retries by
 * itself: sending is still the learner's own press of «ارسال برای تحلیل».
 * What the outbox adds is that the take is still there to press it on after
 * a reload, and that pressing it twice cannot make two attempt rows.
 *
 * R13 (five repetitions, owner decision 2026-10-13): with `repeat.count`
 * above 1 (`me_profile.features.repeatCount`), every stopped recording is
 * checked first. While it was recorded the on-device recognizer typed what
 * it heard (`useLiveText`); a repetition counts when that heard EVERY word
 * of the sentence in order (owner decision 2026-10-15, «همهٔ کلمات»:
 * `allWordsCheck`, the shared rule — contractions and numbers in one
 * spelling, extra words free) — or, without a recognizer, or with the
 * threshold at 0 (the emergency switch), when it had speech in it at all.
 * Each verdict plays one of Sobhan's cues (`lib/cues.js`: rejected, counted,
 * the counted 5th — that one with the confetti, `celebration`), and once the
 * cue is over the reference plays again for the next repetition. A video
 * lesson replays only its sound track then, never the picture (Sobhan's
 * «Edit (Admin Domain)», 2026-10-13). Only the last counted repetition is
 * kept, put in the outbox and sent; the others are discarded the moment they
 * are checked, and the upload reports only how many there were and how long
 * they took.
 */
export function useShadow(lesson, preferredIndex = null, initialSpeed = 1, { outbox = defaultOutbox, repeat = NO_REPEAT } = {}) {
  const audio = useAudio()
  const audioRef = useRef(null)
  // The reference never plays into an open microphone (lib/referenceGuard.js):
  // armed by the tap itself, so nothing waits on a render to know a take is on.
  const guardRef = useRef(null)
  if (guardRef.current === null) guardRef.current = createReferenceGuard()
  const abortRef = useRef(null)
  // The take's `clientAttemptId`, minted here (the reducer stays pure) and
  // kept in a ref so the microphone re-sync below hands the reducer the id
  // the take already has rather than a fresh one on every pass.
  const takeIdRef = useRef(null)
  // What the microphone was doing at the last commit. The outbox read below
  // is asynchronous, and a learner can start recording while it is still in
  // flight; this is how that read knows not to put an old take on top.
  const audioStateRef = useRef(audio.state)
  // The settings page's default speed (P-settings) is where playback starts; the pills move it from there.
  const [session, dispatch] = useReducer(reduce, initialSpeed, initialState)
  // The session as of the last render, for effects that must not re-run on it.
  const sessionRef = useRef(session)
  sessionRef.current = session
  const { speed, playing, position, sendPhase, takeId, queued } = session

  const submitRecording = useLearningStore((s) => s.submitRecording)
  const analyzing = useLearningStore((s) => s.analyzing)
  const result = useLearningStore((s) => s.result)

  const sentence = useMemo(() => practiceSentence(lesson, preferredIndex), [lesson, preferredIndex])

  // R13: the server's repetition count, taken before the practice starts.
  useEffect(() => {
    dispatch({ type: 'reps/configure', count: repeat.count })
  }, [repeat.count])
  const repeating = isRepeating(session)
  // The live text types every take, word by word — repeating or not (owner,
  // 2026-10-14: «هنگام هربار ضبط هم باید متن کلمه به کلمه روی صفحه براش تایپ بشه»).
  const liveTextOn = Boolean(sentence)
  const liveText = useLiveText(sentence?.textEn, liveTextOn)
  const liveTextRef = useRef(liveText)
  liveTextRef.current = liveText
  const { setChunkListener } = audio
  useEffect(() => {
    setChunkListener(liveTextOn ? liveText.feed : null)
    return () => setChunkListener(null)
  }, [liveTextOn, liveText.feed, setChunkListener])
  // The outbox's takes belong to the learner who made them.
  const userId = useAuthStore((s) => s.user?.id ?? null)
  // A restored take keeps its own age when it is written back (the outbox's one-day limit).
  const restoredAtRef = useRef(null)

  // What the element plays for the chosen speed: the server's pre-rendered
  // file at 1x when it has one, else the 1x file stretched (lib/speedVariants).
  // A lesson with a video plays the video; the reference audio otherwise.
  const referenceMedia = useMemo(() => {
    if (lesson?.videoUrl) return mediaSourceFor(lesson.videoUrl, lesson.videoVariants, speed)
    return mediaSourceFor(sentence?.referenceAudioUrl, sentence?.referenceAudioVariants, speed)
  }, [lesson, sentence, speed])

  // Where the element was, as a fraction of its length, kept up to date so a
  // change of file (a new speed with a rendered file) can resume from the
  // same place: the rendered file is longer or shorter, so seconds do not
  // carry over but the fraction does.
  const progressRef = useRef({ src: '', fraction: 0, playing: false })
  const pendingSeekRef = useRef(null)
  useEffect(() => {
    const last = progressRef.current
    if (last.src && last.src !== referenceMedia.src) {
      pendingSeekRef.current = { fraction: last.fraction, resume: last.playing }
    }
    progressRef.current = { ...last, src: referenceMedia.src }
  }, [referenceMedia.src])

  // The microphone's own state says whether a take exists — it also stops
  // itself at the cap, with no button pressed — so the session follows
  // `useAudio` rather than the callbacks. The reducer ignores a re-sync
  // that changes nothing (the mount, `requesting` → `ready`).
  useEffect(() => {
    const previous = audioStateRef.current
    audioStateRef.current = audio.state
    // The take is over (stopped, at the cap, or thrown away): the reference may play again.
    if (previous === 'recording' && audio.state !== 'recording') guardRef.current.disarm()
    if (audio.state === 'recording') {
      restoredAtRef.current = null
      dispatch({ type: 'recording/started' })
    } else if (audio.state === 'recorded') {
      takeIdRef.current = takeIdRef.current ?? newTakeId()
      // Measured from the samples; a take adopted from the outbox has none and is not judged by length.
      const seconds = audio.samples ? audio.samples.length / 16000 : undefined
      dispatch({ type: 'recording/stopped', takeId: takeIdRef.current, seconds })
      if (seconds !== undefined && seconds < MIN_TAKE_SECONDS) {
        // A slip of the finger: not a try (the reducer said so), and no take to keep.
        audio.discard()
      } else if (seconds !== undefined && !isRepeating(sessionRef.current)) {
        // A single take: its live text is complete now (a repetition's is read by its check).
        void liveTextRef.current.end()
      }
    } else {
      takeIdRef.current = null
      dispatch({ type: 'recording/discarded' })
    }
    // Only on the microphone's state: `audio` is a fresh object every render,
    // and its samples and discard are read as they are at the stop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audio.state])

  // A new recording goes into the outbox as soon as it exists, so a reload —
  // or a backgrounded tab the browser throws away — does not lose it. A take
  // restored by the effect below is written again under the same key, which
  // replaces the record rather than adding a second one.
  //
  // Only once the take is in the review: a repetition being checked (R13)
  // may still be thrown away, and only the last one of a practice is kept.
  const inReview = session.phase === 'reviewing'
  useEffect(() => {
    if (!inReview || !audio.blob || !lesson || !sentence || !takeIdRef.current) return
    void outbox.save({
      id: takeIdRef.current,
      userId,
      lessonId: lesson.id,
      sentenceIndex: sentence.index,
      blob: audio.blob,
      filename: audio.filename,
      recordedSeconds: audio.recordedSeconds || audio.seconds,
      createdAt: restoredAtRef.current ?? Date.now(),
      ...localPractice(session),
    })
    // The blob identifies the take; following the second counter as well
    // would rewrite the same WAV once a second while it is being recorded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inReview, audio.blob, lesson?.id, sentence?.index, outbox])

  // The other half: a take the outbox kept from an earlier visit comes back
  // into the review when the sentence opens with no live recording in hand.
  useEffect(() => {
    if (!lesson || !sentence || !userId) return
    let cancelled = false
    void outbox
      // Only this learner's take, and only one younger than a day (PM audit 2026-10-14).
      .pendingFor(lesson.id, sentence.index, { userId, now: Date.now() })
      .then((record) => {
        if (cancelled || !record?.blob) return
        // A take made while the database was being read wins: it is the one
        // the learner is looking at.
        if (audioStateRef.current === 'recording' || audioStateRef.current === 'recorded') return
        // R13: a take kept from a practice without the repetitions this one
        // needs (a single take, or one from before R13) is not this practice's
        // last — it stays in the outbox, unshown, and the practice starts at
        // repetition 1. The reducer refuses it too.
        if (!restorable(repeat.count, record)) return
        takeIdRef.current = record.id
        restoredAtRef.current = record.createdAt ?? null
        // Into the review first: `adopt` never passes through `recording`, so
        // the microphone re-sync alone would leave the session idle and the
        // restored take unsendable.
        dispatch({ type: 'take/restored', takeId: record.id, localReps: record.localReps, localSeconds: record.localPracticeSeconds ?? 0 })
        audio.adopt(record.blob, record.recordedSeconds ?? 0)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
    // Only on the sentence: `audio` is a fresh object every render, and
    // `adopt` refuses anyway once a take is in hand.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson?.id, sentence?.index, outbox, userId])

  // Keep the element in sync when the speed control changes mid-playback.
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = referenceMedia.playbackRate
  }, [referenceMedia.playbackRate])

  const setSpeed = useCallback((value) => dispatch({ type: 'speed/set', speed: value }), [])
  const setPosition = useCallback((value) => dispatch({ type: 'reference/position', position: value }), [])

  const playReference = useCallback(async () => {
    const element = audioRef.current
    if (!element) return
    if (guardRef.current.recording || audio.isRecording) return // never play into an open microphone
    element.playbackRate = referenceMedia.playbackRate
    await element.play()
    dispatch({ type: 'reference/played' })
  }, [audio.isRecording, referenceMedia.playbackRate])

  const pauseReference = useCallback(() => {
    audioRef.current?.pause()
    dispatch({ type: 'reference/paused' })
  }, [])

  const replay = useCallback(() => {
    const element = audioRef.current
    if (!element) return
    element.currentTime = 0
    dispatch({ type: 'reference/rewound' })
    void playReference()
  }, [playReference])

  // R13: the sentence's own sound track, for the replays between repetitions
  // of a video lesson — the picture is for the first viewing only.
  const replayAudioRef = useRef(null)
  const sentenceAudio = useMemo(
    () => (sentence?.referenceAudioUrl ? mediaSourceFor(sentence.referenceAudioUrl, sentence.referenceAudioVariants, speed) : null),
    [sentence, speed],
  )
  const stopReplayAudio = useCallback(() => {
    replayAudioRef.current?.pause()
  }, [])

  const startRecording = useCallback(async () => {
    // First, before anything is awaited: from this tap on no reference plays,
    // and a replay already scheduled after the last cue is void.
    guardRef.current.arm()
    pauseReference()
    stopReplayAudio()
    liveText.begin()
    const started = await audio.start()
    if (!started) guardRef.current.disarm()
    // The cues are fetched while the first repetition is spoken, so its verdict is heard on time.
    if (started && repeating) preloadCues(audio.context())
  }, [audio, pauseReference, stopReplayAudio, repeating, liveText])

  /**
   * The reference again, from the start, for the next repetition (R13). A
   * video lesson plays only its sound track — the sentence's reference audio,
   * which the server cuts from the video — and falls back to the video only
   * for a lesson that has no such track yet.
   */
  const replayForRepetition = useCallback(async () => {
    if (guardRef.current.recording || audioStateRef.current === 'recording') return
    if (lesson?.videoUrl && sentenceAudio?.src) {
      audioRef.current?.pause()
      const element = replayAudioRef.current ?? new Audio()
      replayAudioRef.current = element
      guardRef.current.watch(element)
      if (element.src !== new URL(sentenceAudio.src, window.location.href).href) element.src = sentenceAudio.src
      element.currentTime = 0
      element.playbackRate = sentenceAudio.playbackRate
      await element.play()
      return
    }
    const element = audioRef.current
    if (!element) return
    element.currentTime = 0
    dispatch({ type: 'reference/rewound' })
    await playReference()
  }, [lesson?.videoUrl, sentenceAudio, playReference])

  // Sobhan, 2026-10-16: once the live text has heard every word, the take
  // stops by itself after a short tail (so the last word is not cut — the
  // fifth take goes to the analysis). A partial result the recognizer takes
  // back before the tail is over cancels it.
  const heardAll = autoStopReady({
    repeating,
    threshold: repeat.threshold,
    recording: audio.isRecording,
    liveStatus: liveText.status,
    textEn: sentence?.textEn,
    heard: liveText.words,
  })
  const stopTake = audio.stop
  useEffect(() => {
    if (!heardAll) return undefined
    const timer = setTimeout(() => {
      if (audioStateRef.current === 'recording') stopTake()
    }, AUTO_STOP_TAIL_MS)
    return () => clearTimeout(timer)
  }, [heardAll, stopTake])

  // R13: the confetti on the accepted 5th repetition — a counter the page's
  // <Confetti> follows, bumped in step with the `rep_final_ok` cue.
  const [celebration, setCelebration] = useState(0)

  // The check outlives the `checking` phase it starts in (its own dispatch
  // ends that phase), so what may not happen after the page is gone is
  // guarded by this rather than by the effect's cleanup.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      stopReplayAudio()
    }
  }, [stopReplayAudio])

  // R13: the check of a stopped repetition. What the recognizer heard is
  // matched against the sentence; without a recognizer, the take's own
  // samples say whether there was speech at all. Read through a ref: the
  // check runs once per stop, against the values of that moment.
  const checkRef = useRef({})
  checkRef.current = { audio, liveText, repeat, session, replayForRepetition, sentence }
  const checking = session.phase === 'checking'
  useEffect(() => {
    if (!checking) return
    void (async () => {
      const { audio: mic, liveText: live, repeat: rule, session: before, sentence: said } = checkRef.current
      const heard = await live.end()
      if (!mountedRef.current) return
      // Owner decision 2026-10-15 («همهٔ کلمات»): any threshold above 0 now means
      // every word of the sentence, in order; 0 stays the emergency «voice alone».
      const check = heard === null ? null : allWordsCheck(said?.textEn, heard)
      const counted = rule.threshold > 0 && check !== null ? check.counted : speechHeard(mic.samples) || (heard?.length ?? 0) > 0
      const last = counted && before.repsDone + 1 >= before.repsRequired
      dispatch({ type: 'rep/checked', counted, ratio: check?.ratio ?? null, words: check?.words ?? null, seconds: mic.recordedSeconds })
      // A tap on the microphone from here on voids the replay below.
      const replayToken = guardRef.current.plan()
      const cue = playCue(mic.context(), cueFor({ counted, last }))
      if (last) {
        setCelebration((n) => n + 1)
        return
      }
      // Not the take to send: it goes, and once the cue is over the reference plays again.
      mic.discard()
      const cueSeconds = await cue
      await new Promise((resolve) => setTimeout(resolve, cueSeconds * 1000 + REPLAY_AFTER_CUE_MS))
      if (!mountedRef.current || !guardRef.current.mayReplay(replayToken)) return
      await checkRef.current.replayForRepetition().catch(() => {})
    })()
  }, [checking])

  // Which phases may send: the reducer's own rule, asked before the request
  // rather than only after it. Without this a second press while the take is
  // already in flight — or after plan B queued it — would still put a POST on
  // the wire that the reducer then ignored. The server would answer the row it
  // already has (same `clientAttemptId`), so nothing would break; it is simply
  // a request with nothing to gain.
  const sendable = hasTake(session)

  const discardAudio = audio.discard
  const submit = useCallback(async ({ transcriptOpenedLocally = false } = {}) => {
    if (!audio.blob || !lesson || !sentence || !sendable) return null
    const controller = new AbortController()
    abortRef.current = controller
    const clientAttemptId = takeIdRef.current
    dispatch({ type: 'submit/started' })
    try {
      const outcome = await submitRecording(lesson.id, sentence.index, audio.blob, audio.filename, {
        signal: controller.signal,
        clientAttemptId,
        ...localPractice(session),
        ...(transcriptOpenedLocally === true ? { transcriptOpenedLocally: true } : {}),
        onUploadProgress: (event) => {
          if (event.total && event.loaded >= event.total) dispatch({ type: 'submit/uploaded' })
        },
      })
      // Either way the server now holds this recording, so the device need
      // not keep it: for the outbox a queue receipt is as final as a score.
      if (clientAttemptId) void outbox.remove(clientAttemptId)
      dispatch(isQueuedAnswer(outcome) ? { type: 'submit/queued', queued: outcome } : { type: 'submit/succeeded' })
      return outcome
    } catch (error) {
      // A cancelled submit is the learner's own choice, not a failure.
      dispatch(controller.signal.aborted ? { type: 'submit/cancelled' } : { type: 'submit/failed', error })
      // repetitions_required: the server will never take this recording, so
      // it leaves the device too (the session already went back to rep 1).
      if (!controller.signal.aborted && classifyFailure(error) === 'repetitions') {
        if (clientAttemptId) void outbox.remove(clientAttemptId)
        discardAudio()
      }
      throw error
    } finally {
      abortRef.current = null
    }
  }, [audio.blob, audio.filename, discardAudio, lesson, outbox, sendable, sentence, session, submitRecording])

  /** «لغو» on the sending panel: abort the wait; the take stays for another try. */
  const cancelSubmit = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  /** «ضبط دوباره»: the take goes, and it goes out of the outbox with it. */
  const discardRecording = useCallback(() => {
    const id = takeIdRef.current
    if (id) void outbox.remove(id)
    audio.discard()
  }, [audio, outbox])

  const toggleReference = useCallback(() => {
    if (playing) pauseReference()
    else void playReference()
  }, [playing, pauseReference, playReference])

  const bindAudio = useCallback(
    (element) => {
      if (audioRef.current && audioRef.current !== element) guardRef.current.unwatch(audioRef.current)
      audioRef.current = element
      if (!element) return
      guardRef.current.watch(element)
      element.playbackRate = referenceMedia.playbackRate
    },
    [referenceMedia.playbackRate],
  )

  // The new file is ready: pick up where the old one was, and keep playing
  // if it was playing. Nothing pending (first load, a new sentence) leaves
  // the element at the start, as before.
  const onLoadedMetadata = useCallback(
    (event) => {
      const pending = pendingSeekRef.current
      pendingSeekRef.current = null
      const element = event.target
      if (!pending || !Number.isFinite(element.duration)) return
      element.currentTime = pending.fraction * element.duration
      element.playbackRate = referenceMedia.playbackRate
      if (pending.resume) void element.play().catch(() => {})
    },
    [referenceMedia.playbackRate],
  )

  return {
    audio,
    sentence,
    phase: session.phase,
    bindAudio,
    referenceMedia,
    // What a lesson plays besides the element's own file: the sound track a
    // video lesson replays between repetitions (lib/mediaPrefetch.js warms both).
    replayTrackSrc: lesson?.videoUrl ? (sentenceAudio?.src ?? null) : null,
    onLoadedMetadata,
    speed,
    setSpeed,
    playing,
    position,
    setPosition,
    analyzing,
    result,
    queued,
    takeId,
    celebration,
    // R13: where the practice is in its repetitions, and the live text.
    repetition: {
      on: repeating,
      required: session.repsRequired,
      done: session.repsDone,
      number: repNumber(session),
      last: isLastRep(session),
      checking,
      lastRep: session.lastRep,
      // Owner rule 2026-10-14: repetitions the live text did not match — three open the text.
      wrong: session.wrongReps,
    },
    liveText: { status: liveText.status, words: liveText.words, target: liveText.target },
    playReference,
    pauseReference,
    replay,
    startRecording,
    stopRecording: audio.stop,
    discardRecording,
    submit,
    sendPhase,
    cancelSubmit,
    toggleReference,
    onPlay: () => {
      progressRef.current.playing = true
      dispatch({ type: 'reference/played' })
    },
    onPause: () => {
      progressRef.current.playing = false
      dispatch({ type: 'reference/paused' })
    },
    onEnded: () => {
      progressRef.current.playing = false
      dispatch({ type: 'reference/ended' })
    },
    onTimeUpdate: (event) => {
      const { currentTime, duration } = event.target
      if (Number.isFinite(duration) && duration > 0) progressRef.current.fraction = currentTime / duration
      dispatch({ type: 'reference/position', position: currentTime })
    },
  }
}
